import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "../styles/useCustomScrollbar";
import useCustomScrollbar from "../styles/useCustomScrollbar";

export interface DropdownOption {
  value: string;
  label: string;
  subtitle?: string;
}

interface CustomDropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  onSearch?: (searchTerm: string) => void;
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  className?: string;
  dropdownClassName?: string;
  maxHeight?: string;
  renderOption?: (option: DropdownOption) => React.ReactNode;
  filterFunction?: (option: DropdownOption, searchTerm: string) => boolean;
  zIndex?: number;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  options,
  value,
  onChange,
  onSearch,
  placeholder = "Select option",
  disabled = false,
  searchable = false,
  className = "",
  dropdownClassName = "",
  maxHeight = "240px",
  renderOption,
  filterFunction,
  zIndex = 60,
}) => {
  useCustomScrollbar();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [displayValue, setDisplayValue] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update display value when value prop changes
  useEffect(() => {
    const selectedOption = options.find((opt) => opt.value === value);
    setDisplayValue(selectedOption ? selectedOption.label : "");
  }, [value, options]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const inTrigger = dropdownRef.current?.contains(event.target as Node);
      const inList   = listRef.current?.contains(event.target as Node);
      if (!inTrigger && !inList) {
        setIsOpen(false);
        if (searchable) {
          setSearchTerm("");
          const selectedOption = options.find((opt) => opt.value === value);
          setDisplayValue(selectedOption ? selectedOption.label : "");
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [searchable, value, options]);

  // Default filter function
  const defaultFilterFunction = (option: DropdownOption, search: string): boolean => {
    const searchLower = search.toLowerCase();
    const labelMatch = option.label.toLowerCase().includes(searchLower);
    const subtitleMatch = option.subtitle ? option.subtitle.toLowerCase().includes(searchLower) : false;
    return labelMatch || subtitleMatch;
  };

  // Filter options based on search term
  const filteredOptions = searchable
    ? options.filter((option) =>
        filterFunction
          ? filterFunction(option, searchTerm)
          : defaultFilterFunction(option, searchTerm)
      )
    : options;

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (!isOpen && searchable && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleOptionClick = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    if (searchable) {
      setSearchTerm("");
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    setDisplayValue(newSearchTerm);
    if (onSearch) {
      onSearch(newSearchTerm);
    }
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    if (searchable) {
      setIsOpen(true);
      setSearchTerm(displayValue);
    }
  };

  const defaultRenderOption = (option: DropdownOption) => (
    <div className="flex flex-col">
      <span className="text-sm text-[#1F1F1F]">{option.label}</span>
      {option.subtitle && (
        <span className="text-xs text-[#8E8E93] mt-0.5">{option.subtitle}</span>
      )}
    </div>
  );

  // Calculate dropdown position — flip upward when not enough space below
  const rect = dropdownRef.current?.getBoundingClientRect();
  const maxHeightPx = parseInt(maxHeight, 10) || 240;
  const openUpward =
    rect !== undefined &&
    window.innerHeight - rect.bottom < maxHeightPx + 8 &&
    rect.top > window.innerHeight - rect.bottom;

  const listPositionStyle = (width: number): React.CSSProperties =>
    rect
      ? openUpward
        ? {
            position: "fixed",
            bottom: window.innerHeight - rect.top + 4,
            left: rect.left,
            width,
            maxHeight: maxHeight,
            zIndex,
          }
        : {
            position: "fixed",
            top: rect.bottom + 4,
            left: rect.left,
            width,
            maxHeight: maxHeight,
            zIndex,
          }
      : {};

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {searchable ? (
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : displayValue}
          onChange={handleSearchChange}
          onFocus={handleInputFocus}
          disabled={disabled}
          placeholder={placeholder}
          className={`w-full px-3 py-2 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base font-inter font-normal placeholder:text-[#9E9E9E] outline-none focus:border-[#0DB5B5] disabled:bg-gray-50 disabled:cursor-not-allowed transition-colors ${
            disabled ? "text-[#C7C7CC]" : "text-[#1F1F1F]"
          }`}
        />
      ) : (
        <button
          type="button"
          onClick={handleToggle}
          disabled={disabled}
          className={`w-full px-3 py-2 bg-white border border-[#E0E0E0] rounded-md text-sm 3xl:text-base text-left font-inter outline-none focus:border-[#0DB5B5] disabled:bg-gray-50 disabled:cursor-not-allowed flex justify-between items-center transition-colors ${
            disabled ? "text-[#C7C7CC]" : "text-[#1F1F1F]"
          }`}
        >
          <span className={`flex-1 min-w-0 truncate text-sm 3xl:text-base font-inter font-normal ${displayValue ? "text-[#1F1F1F]" : "text-[#9E9E9E]"}`}>
            {displayValue || placeholder}
          </span>
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
          >
            <path
              d="M3 4.5L6 7.5L9 4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      {isOpen && filteredOptions.length > 0 && rect && createPortal(
        <div
          ref={listRef}
          className={`bg-white border border-[#E0E0E0] rounded-md shadow-lg overflow-y-auto custom-scrollbar-overlay ${dropdownClassName}`}
          style={listPositionStyle(rect.width)}
        >
          {filteredOptions.map((option) => (
            <div
              key={option.value}
              onClick={() => handleOptionClick(option.value)}
              className="m-2 px-3 py-2 hover:bg-[#E5F8F8] rounded-md cursor-pointer transition-colors"
            >
              {renderOption ? renderOption(option) : defaultRenderOption(option)}
            </div>
          ))}
        </div>,
        document.body
      )}

      {isOpen && filteredOptions.length === 0 && searchable && searchTerm && rect && createPortal(
        <div
          ref={listRef}
          className={`bg-white border border-[#E0E0E0] rounded-md shadow-lg ${dropdownClassName}`}
          style={listPositionStyle(rect.width)}
        >
          <div className="px-3 py-4 text-center text-sm text-[#8E8E93]">
            No results found
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CustomDropdown;
